import React, { useState, useEffect, useCallback } from 'react';
import { Navbar, Nav, Container } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faMobileScreenButton } from '@fortawesome/free-solid-svg-icons';

const NAV_OFFSET = 90;

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);

      if (location.pathname !== '/') return;
      const ids = ['home', 'about', 'services', 'provider', 'how', 'stories', 'financing', 'faq', 'booking', 'contact'];
      const probe = window.scrollY + NAV_OFFSET + 20;

      // Build a list of {id, top} sorted by actual DOM offsetTop so the
      // scrollspy works correctly regardless of array order. Then pick the
      // last section whose top is at or above the probe line.
      const sectionPositions = ids
        .map((id) => {
          const el = document.getElementById(id);
          return el ? { id, top: el.offsetTop } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.top - b.top);

      let current = 'home';
      for (const { id, top } of sectionPositions) {
        if (top <= probe) current = id;
        else break;
      }
      setActiveSection(current);

      // Keep the URL hash in sync as the user scrolls (without spamming history)
      const desiredHash = current === 'home' ? '' : `#${current}`;
      if (window.location.hash !== desiredHash) {
        const newUrl = `${window.location.pathname}${window.location.search}${desiredHash}`;
        window.history.replaceState(null, '', newUrl);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Collapse mobile menu when route changes
  useEffect(() => {
    setExpanded(false);
  }, [location.pathname]);

  // After navigating to "/" with a hash, scroll to the target section.
  // The home page loads its services/doctors asynchronously and images load
  // afterwards, so the page height keeps growing for a moment. A single early
  // scroll lands on the wrong section. We therefore re-align to the target as
  // the layout settles (observing body size changes) for a short window, and
  // stop as soon as the user scrolls manually.
  useEffect(() => {
    if (location.pathname !== '/' || !location.hash) return;

    const id = location.hash.slice(1);
    let cancelled = false;
    let userInterrupted = false;

    const onUserScroll = () => { userInterrupted = true; };
    window.addEventListener('wheel', onUserScroll, { passive: true });
    window.addEventListener('touchmove', onUserScroll, { passive: true });
    window.addEventListener('keydown', onUserScroll);

    const scrollToTarget = (behavior) => {
      if (cancelled || userInterrupted) return;
      const el = document.getElementById(id);
      if (el) {
        window.scrollTo({ top: el.offsetTop - NAV_OFFSET, behavior });
      }
    };

    // Initial (smooth) attempt once the DOM has had a tick to render.
    const startT = setTimeout(() => scrollToTarget('smooth'), 60);

    // Re-align instantly whenever the page height changes (async content /
    // images loading in), so we stay locked on the correct section.
    const ro = new ResizeObserver(() => scrollToTarget('auto'));
    ro.observe(document.body);

    // Stop correcting after the layout has had time to settle.
    const stopT = setTimeout(() => {
      cancelled = true;
      ro.disconnect();
    }, 1500);

    return () => {
      cancelled = true;
      clearTimeout(startT);
      clearTimeout(stopT);
      ro.disconnect();
      window.removeEventListener('wheel', onUserScroll);
      window.removeEventListener('touchmove', onUserScroll);
      window.removeEventListener('keydown', onUserScroll);
    };
  }, [location.pathname, location.hash]);

  const scrollToSection = useCallback((id) => (e) => {
    if (e) e.preventDefault();
    setExpanded(false);
    if (location.pathname !== '/') {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      // Push the hash to the URL so it's shareable / bookmarkable
      const targetHash = id === 'home' ? '' : `#${id}`;
      const newUrl = `${window.location.pathname}${window.location.search}${targetHash}`;
      if (`${window.location.pathname}${window.location.search}${window.location.hash}` !== newUrl) {
        window.history.pushState(null, '', newUrl);
      }
      window.scrollTo({
        top: el.offsetTop - NAV_OFFSET,
        behavior: 'smooth'
      });
    }
  }, [location.pathname, navigate]);

  const sectionLinkStyle = {
    transition: 'all 0.3s ease',
    fontSize: '14px',
    fontWeight: '500',
    whiteSpace: 'nowrap',
    cursor: 'pointer'
  };

  return (
    <Navbar 
      expand="xxl" 
      fixed="top" 
      className={`${scrolled ? 'shadow-sm scrolled' : ''} py-3 navbar-premium`}
      style={{ 
        transition: 'all 0.35s ease',
        backdropFilter: scrolled ? 'blur(16px) saturate(180%)' : 'blur(8px)',
        backgroundColor: scrolled ? 'rgba(253, 252, 249, 0.92)' : 'rgba(253, 252, 249, 0.7)',
        borderBottom: scrolled ? '1px solid rgba(230, 224, 212, 0.6)' : '1px solid transparent'
      }}
      expanded={expanded}
    >
      <Container>
        <Navbar.Brand as={Link} to="/" onClick={scrollToSection('home')}>
          <div className="d-flex align-items-center">
            <img 
              src="/images/logo.png" 
              alt="Life and Wellness Center Logo"
              height="40" 
              width="auto"
              style={{ maxWidth: '45px' }}
              className="me-2"
            />
            <div className="m-0" style={{
              fontSize: '22px',
              fontWeight: '600',
              fontFamily: "'Playfair Display', serif",
              letterSpacing: '-0.01em'
            }}>
              <span style={{ color: 'var(--primary-color)' }}>Life</span>{' '}
              <span style={{ color: 'var(--text-light)', fontStyle: 'italic', fontWeight: 400 }}>&amp;</span>{' '}
              <span style={{ color: 'var(--secondary-color)' }}>Wellness</span>{' '}
              <span style={{ color: 'var(--primary-dark)' }}>Center</span>
            </div>
          </div>
        </Navbar.Brand>
        <Navbar.Toggle 
          aria-controls="basic-navbar-nav" 
          className="border-0"
          onClick={() => setExpanded(prev => !prev)}
        >
          <FontAwesomeIcon icon={faBars} />
        </Navbar.Toggle>
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="mx-auto align-items-center">
            <Nav.Link
              href="/#home"
              onClick={scrollToSection('home')}
              className={`mx-1 position-relative ${location.pathname === '/' && activeSection === 'home' ? 'active' : ''}`}
              style={sectionLinkStyle}
            >
              Home
            </Nav.Link>
            <Nav.Link
              href="/#about"
              onClick={scrollToSection('about')}
              className={`mx-1 position-relative ${location.pathname === '/' && activeSection === 'about' ? 'active' : ''}`}
              style={sectionLinkStyle}
            >
              About Us
            </Nav.Link>
            <Nav.Link
              href="/#services"
              onClick={scrollToSection('services')}
              className={`mx-1 position-relative ${location.pathname === '/' && activeSection === 'services' ? 'active' : ''}`}
              style={sectionLinkStyle}
            >
              Services
            </Nav.Link>
            <Nav.Link
              as={Link}
              to="/immigration-medical-exam-odessa-tx"
              onClick={() => setExpanded(false)}
              className={`mx-1 position-relative ${location.pathname === '/immigration-medical-exam-odessa-tx' ? 'active' : ''}`}
              style={sectionLinkStyle}
            >
              Immigration
            </Nav.Link>
            <Nav.Link
              href="/#provider"
              onClick={scrollToSection('provider')}
              className={`mx-1 position-relative ${location.pathname === '/' && activeSection === 'provider' ? 'active' : ''}`}
              style={sectionLinkStyle}
            >
              Our Care Team
            </Nav.Link>
            <Nav.Link
              href="/#how"
              onClick={scrollToSection('how')}
              className={`mx-1 position-relative ${location.pathname === '/' && activeSection === 'how' ? 'active' : ''}`}
              style={sectionLinkStyle}
            >
              How It Works
            </Nav.Link>
            <Nav.Link
              href="/#stories"
              onClick={scrollToSection('stories')}
              className={`mx-1 position-relative ${location.pathname === '/' && activeSection === 'stories' ? 'active' : ''}`}
              style={sectionLinkStyle}
            >
              Reviews
            </Nav.Link>
            <Nav.Link
              href="/#financing"
              onClick={scrollToSection('financing')}
              className={`mx-1 position-relative ${location.pathname === '/' && activeSection === 'financing' ? 'active' : ''}`}
              style={sectionLinkStyle}
            >
              Financing
            </Nav.Link>
            <Nav.Link
              href="/#faq"
              onClick={scrollToSection('faq')}
              className={`mx-1 position-relative ${location.pathname === '/' && activeSection === 'faq' ? 'active' : ''}`}
              style={sectionLinkStyle}
            >
              FAQ
            </Nav.Link>
            <Nav.Link
              href="/#contact"
              onClick={scrollToSection('contact')}
              className={`mx-1 position-relative ${location.pathname === '/' && activeSection === 'contact' ? 'active' : ''}`}
              style={sectionLinkStyle}
            >
              Contact
            </Nav.Link>
          </Nav>
          <div className="d-flex align-items-center justify-content-center mt-3 mt-lg-0">
            <a
              href="http://lifeandwellnesscenter.repeatmd.app"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-view-app"
              onClick={() => setExpanded(false)}
              style={{ whiteSpace: 'nowrap' }}
            >
              <FontAwesomeIcon icon={faMobileScreenButton} className="me-2" />
              View Our App
            </a>
          </div>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default Header;